import { Component, computed, ElementRef, OnInit, signal } from '@angular/core';

import { MARKETING_SITE_DOMAIN } from 'Angular/constants/core.constants';
import { AngularUser } from 'Angular/interfaces/angular-user.interface';

import { TrialSignupValidationService } from '../trial-signup-validation.service';
import { UrlsService } from 'Angular/services/core/urls.service';
import { CookieService } from 'ngx-cookie-service';

interface SignupFormComponentErrorData {
    email: string | null;
    password: string | null; // pragma: allowlist secret
    phone: string | null;
}

interface SignupFormComponentFormData {
    email: string;
    firstName: string;
    jobFunction: string;
    lastName: string;
    organization: string;
    password: string; // pragma: allowlist secret
    phone: string;
    terms: boolean;
}

@Component({ standalone: false, selector: 'ycn-trial-signup-form', templateUrl: './signup-form.template.html' })
export class SignupFormComponent implements OnInit {
    actionUrl: string = '';

    csrfToken: string = '';

    disabledInputs: string[];

    DUPLICATE_EMAIL_ERROR = 'A user with this email address already exists. Please use a different email.';

    errors: SignupFormComponentErrorData = { email: null, password: null, phone: null }; // pragma: allowlist secret

    form: SignupFormComponentFormData = {
        email: '',
        firstName: '',
        jobFunction: '',
        lastName: '',
        organization: '',
        password: '', // pragma: allowlist secret
        phone: '',
        terms: false,
    };

    _formHeader: string = '';

    _formSubtitle: string = '';

    get showHeader(): boolean {
        return !!this._formHeader;
    }

    readonly formHeader = computed(() => {
        if (this.emailSubmitted()) {
            return 'Let’s personalize your experience';
        }
        return this._formHeader;
    });

    readonly formSubtitle = computed(() => {
        if (this.emailSubmitted()) {
            return 'We use this information to provide you with a better experience';
        }
        return this._formSubtitle;
    });

    googleLoginUrl: string | null = null;

    isSaving: boolean = false;

    linkAccountUrl: string | null = null;

    linkedinLoginUrl: string | null = null;

    loggedIn: boolean = false;

    next: string | null = null;

    socialSignup: boolean = false;

    ssoSignup: boolean = false;

    readonly emailSubmitted = signal<boolean>(false);

    readonly emailValidationPending = signal<boolean>(false);

    termsUrl: string = `${MARKETING_SITE_DOMAIN}/terms-of-use/`;

    USER: AngularUser = window.USER;

    constructor(
        private cookieService: CookieService,
        el: ElementRef,
        private urlsService: UrlsService,
        private validationService: TrialSignupValidationService
    ) {
        this.actionUrl = el.nativeElement.getAttribute('action-url');
        this.socialSignup = el.nativeElement.getAttribute('social-signup') === 'True';
        this.ssoSignup = el.nativeElement.getAttribute('sso-signup') === 'True';

        this.form.email = el.nativeElement.getAttribute('email') || '';
        this.form.firstName = el.nativeElement.getAttribute('first-name') || '';
        this.form.jobFunction = el.nativeElement.getAttribute('job-function') || '';
        this.form.lastName = el.nativeElement.getAttribute('last-name') || '';
        this.form.organization = el.nativeElement.getAttribute('organization') || '';
        this.form.password = el.nativeElement.getAttribute('password') || '';
        this.form.phone = el.nativeElement.getAttribute('phone-number') || '';
        this.form.terms = el.nativeElement.getAttribute('terms') || false;

        this.disabledInputs = el.nativeElement.getAttribute('disabled-inputs') || [];

        this._formHeader = el.nativeElement.getAttribute('form-header') || '';
        this._formSubtitle = el.nativeElement.getAttribute('form-subtitle') || '';
        this.googleLoginUrl = el.nativeElement.getAttribute('google-login-url');
        this.linkedinLoginUrl = el.nativeElement.getAttribute('linkedin-login-url');
        this.next = el.nativeElement.getAttribute('next');

        const urlParams = new URLSearchParams(window.location.search);
        const ssoIdp = urlParams.get('sso_idp');
        if (this.ssoSignup && ssoIdp) {
            this.linkAccountUrl = this.urlsService.getUrl(this.urlsService.INTEGRATIONS.SSO_LINK_ACCOUNT, { ssoIdp });
        }
    }

    ngOnInit(): void {
        this.csrfToken = this.cookieService.get('csrftoken');
        this.loggedIn = this.USER.authenticated;

        this.validateEmail();
        if (!this.socialSignup) {
            // Social signups never set a password, so validating a blank one would
            // leave the form permanently invalid.
            this.validatePassword();
        }
        this.validatePhone();
    }

    validatePhone(): void {
        this.errors.phone = this.validationService.validatePhone(this.form.phone);
    }

    validateEmail(onValidated?: () => void): void {
        if (!this.form.email) {
            this.errors.email = null;
            return;
        }
        this.emailValidationPending.set(true);
        this.validationService.validateEmail(this.form.email).subscribe((value) => {
            this.errors.email = value;
            this.emailValidationPending.set(false);
            onValidated?.();
        });
    }

    validatePassword(): void {
        this.errors.password = this.validationService.validatePassword(this.form.password);
    }

    isValidForm(): boolean {
        return !Object.values(this.errors).some((x) => x !== null);
    }

    showSocialLogin(): boolean {
        return (
            !!this.googleLoginUrl && !!this.linkedinLoginUrl && !this.loggedIn && !this.socialSignup && !this.ssoSignup
        );
    }

    fieldDisabled(fieldName: string): boolean {
        return this.disabledInputs.includes(fieldName);
    }

    isValidEmailStep(): boolean {
        const emailValid = this.loggedIn || (!!this.form.email && this.errors.email === null);
        const passwordValid =
            this.loggedIn || this.socialSignup || (!!this.form.password && this.errors.password === null);
        return emailValid && passwordValid;
    }

    submitEmail(): void {
        if (!this.socialSignup) {
            this.validatePassword();
        }
        const advanceIfValid = (): void => {
            if (this.isValidEmailStep()) {
                this.emailSubmitted.set(true);
            }
        };
        // Re-validate the email so we never advance on a stale or in-flight result.
        if (this.loggedIn || !this.form.email) {
            advanceIfValid();
        } else {
            this.validateEmail(advanceIfValid);
        }
    }

    editEmail(): void {
        this.emailSubmitted.set(false);
    }

    onSubmit(e: Event): void {
        this.isSaving = true;
        const target = e.target as HTMLFormElement;
        target.submit();
    }
}
